import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { wishlistAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Heart, Trash2, ArrowDown, ArrowUp, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import ResponsiveImage from '../components/ResponsiveImage';
import DealerBadge from '../components/DealerBadge';
import TrustedSellerBadge from '../components/TrustedSellerBadge';
import useScrollRestore, { saveFeedScroll } from '../hooks/useScrollRestore';

// iter114 — status → user-facing label + Tailwind chip colours.
// Every state defined in the Wishlist requirements gets a distinct visual
// treatment so users understand *why* an item is unavailable.
const STATUS_META = {
  active:                { label: 'Available',              chip: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200' },
  sold:                  { label: 'SOLD',                   chip: 'bg-red-600 text-white' },
  subscription_expired:  { label: 'Temporarily Unavailable', chip: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200' },
  archived:              { label: 'Currently Unavailable',  chip: 'bg-slate-200 text-slate-700 ring-1 ring-slate-300' },
  listing_removed:       { label: 'Listing Removed',        chip: 'bg-slate-200 text-slate-700 ring-1 ring-slate-300' },
  admin_removed:         { label: 'No Longer Available',    chip: 'bg-slate-200 text-slate-700 ring-1 ring-slate-300' },
  seller_gone:           { label: 'Seller Unavailable',     chip: 'bg-slate-200 text-slate-700 ring-1 ring-slate-300' },
};

const PLACEHOLDER_COPY = {
  listing_removed: 'This listing has been removed by the seller.',
  admin_removed:   'This listing is no longer available.',
  seller_gone:     'Seller is no longer available.',
};

const PLACEHOLDER_STATES = new Set(['listing_removed', 'admin_removed', 'seller_gone']);

const Wishlist = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useScrollRestore('wishlist', { ready: items.length > 0 });

  useEffect(() => {
    // Wait for AuthContext to settle before deciding to redirect — otherwise
    // a hard-reload on /wishlist bounces to /login for a split-second before
    // the /auth/me lookup returns.
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    (async () => {
      try {
        const res = await wishlistAPI.getAll();
        setItems(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Failed to load wishlist:', e);
        toast.error('Failed to load wishlist');
      } finally { setLoading(false); }
    })();
  }, [user, authLoading, navigate]);

  const handleRemove = async (item) => {
    try {
      await wishlistAPI.remove(item.listing_id);
      setItems((prev) => prev.filter((i) => i.wishlist_id !== item.wishlist_id));
      toast.success('Removed from wishlist');
    } catch (e) {
      console.error('Failed to remove from wishlist:', e);
      toast.error('Failed to remove item');
    }
  };

  const openListing = async (item) => {
    // Only interactive rows should navigate — and mark-seen clears the
    // price-change badge next time the wishlist is loaded.
    if (!item.can_interact) return;
    saveFeedScroll('wishlist');
    if (item.price_change && item.price_change.direction !== 'same') {
      try { await wishlistAPI.markSeen(item.listing_id); } catch (_) { /* silent */ }
    }
    navigate(`/listing/${item.listing_id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-[#1800ad] mx-auto mb-4"></div>
          <p className="text-[#4B5563] font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-8">
          <Link to="/" className="text-[#4B5563] hover:text-[#1800ad] font-medium mb-4 inline-block" data-testid="back-link">
            ← Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Heart size={32} className="text-red-500" fill="#ef4444" />
            <h1 className="text-4xl font-black text-[#0A0B10]" style={{ fontFamily: 'Google Sans Flex' }} data-testid="wishlist-title">
              My Wishlist
            </h1>
          </div>
          <p className="text-[#4B5563]">Your saved items ({items.length})</p>
        </div>

        {items.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center shadow-lg">
            <Heart size={64} className="mx-auto mb-4 text-gray-300" />
            <p className="text-xl font-bold text-[#0A0B10] mb-2">Your wishlist is empty</p>
            <p className="text-[#4B5563] mb-6">Start saving items you love!</p>
            <Link to="/" className="inline-block bg-[#1800ad] text-white rounded-full px-8 py-3 font-bold hover:bg-[#1200a0] transition-colors">
              Browse Listings
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start" data-testid="wishlist-grid">
            {items.map((item) => (
              <WishlistCard key={item.wishlist_id} item={item} onRemove={handleRemove} onOpen={openListing} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const priceBadge = (pc) => {
  if (!pc || pc.direction === 'same') return null;
  const isDrop = pc.direction === 'dropped';
  return (
    <div
      data-testid={`price-change-${isDrop ? 'dropped' : 'increased'}`}
      className={`absolute top-2 left-2 z-10 flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold shadow ${isDrop ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'}`}
    >
      {isDrop ? <ArrowDown size={11} strokeWidth={3} /> : <ArrowUp size={11} strokeWidth={3} />}
      {isDrop ? 'Price Dropped' : 'Price Updated'}
      {pc.previous_price != null && (
        <span className="line-through opacity-80 ml-0.5 font-medium">₹{pc.previous_price.toLocaleString()}</span>
      )}
    </div>
  );
};

const WishlistCard = ({ item, onRemove, onOpen }) => {
  const meta = STATUS_META[item.status] || STATUS_META.active;
  const isPlaceholder = PLACEHOLDER_STATES.has(item.status);
  const isActive = item.can_interact;
  // Greyed cards for anything the user cannot chat/call about.
  const greyed = !isActive;

  // ── Placeholder card (listing_removed / admin_removed / seller_gone) ──
  if (isPlaceholder) {
    const snapTitle = (item.snapshot && item.snapshot.title) || 'Saved listing';
    return (
      <div
        data-testid={`wishlist-item-${item.listing_id}`}
        data-status={item.status}
        className="bg-white rounded-3xl p-4 shadow-md opacity-70 grayscale"
      >
        <div className="w-full h-48 bg-gray-100 rounded-2xl mb-4 flex flex-col items-center justify-center px-4 text-center">
          <AlertCircle size={40} className="text-gray-400 mb-2" />
          <p className="text-sm text-[#4B5563] font-medium leading-snug">{PLACEHOLDER_COPY[item.status]}</p>
        </div>
        <h3 className="text-base font-bold text-[#0A0B10] mb-2 line-clamp-2">{snapTitle}</h3>
        <span data-testid={`status-badge-${item.listing_id}`} className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${meta.chip}`}>
          {meta.label}
        </span>
        <button
          data-testid={`remove-${item.listing_id}`}
          onClick={() => onRemove(item)}
          className="w-full mt-4 bg-red-50 text-red-600 rounded-full px-4 py-2 font-bold hover:bg-red-600 hover:text-white transition-colors flex items-center justify-center gap-2"
        >
          <Trash2 size={16} /> Remove
        </button>
      </div>
    );
  }

  // ── Normal card (active / sold / subscription_expired / archived) ──
  const listing = item.listing || {};
  const image0 = (listing.images || [])[0];

  return (
    <div
      data-testid={`wishlist-item-${item.listing_id}`}
      data-status={item.status}
      className={`brand-gradient-border-card rounded-3xl p-4 hover:shadow-xl ${listing.is_dealer_listing ? 'dealer-card' : ''} ${greyed ? 'opacity-70 grayscale' : ''}`}
    >
      <div className={`relative ${isActive ? 'cursor-pointer' : 'cursor-default'}`} onClick={() => onOpen(item)}>
        {priceBadge(item.price_change)}

        {/* Status badge — top right. Hidden for `active` items because the
            card is clearly usable and a redundant "AVAILABLE" chip only
            crowds the price-change pill on the left. */}
        {item.status !== 'active' && (
          <span
            data-testid={`status-badge-${item.listing_id}`}
            className={`absolute top-2 right-2 z-10 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide shadow ${meta.chip}`}
          >
            {meta.label}
          </span>
        )}

        {image0 ? (
          <ResponsiveImage
            path={image0}
            alt={listing.title || ''}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="w-full h-48 object-cover rounded-2xl mb-4"
          />
        ) : (
          <div className="w-full h-48 bg-gray-100 rounded-2xl mb-4 flex items-center justify-center">
            <span className="text-gray-400">No Image</span>
          </div>
        )}

        <h3 className="text-lg font-bold text-[#0A0B10] mb-2 line-clamp-2">{listing.title}</h3>
        <p className="text-2xl font-black text-[#1800ad] mb-2" style={{ fontFamily: 'Google Sans Flex' }}>
          ₹{(listing.price || 0).toLocaleString()}
        </p>
        <p className="text-sm text-[#9CA3AF]">{listing.location}</p>
        {(listing.is_dealer_listing || listing.is_trusted_seller) && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {listing.is_dealer_listing && <DealerBadge testId={`dealer-badge-${item.listing_id}`} />}
            {listing.is_trusted_seller && !listing.is_dealer_listing && (
              <TrustedSellerBadge size="sm" short testId={`trusted-badge-${item.listing_id}`} />
            )}
          </div>
        )}
      </div>

      <button
        data-testid={`remove-${item.listing_id}`}
        onClick={() => onRemove(item)}
        className="w-full mt-4 bg-red-50 text-red-600 rounded-full px-4 py-2 font-bold hover:bg-red-600 hover:text-white transition-colors flex items-center justify-center gap-2"
      >
        <Trash2 size={16} /> Remove
      </button>
    </div>
  );
};

export default Wishlist;
